import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, EmptyState, Spinner } from "@/components/ui";
import { fmtDate } from "@/lib/helpers";
import { Lock, FileText, Mic, ExternalLink } from "lucide-react";

const prettySize = (b) => !b ? "" : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;

export default function Vault() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    setErr(null);
    api.blobList()
      .then(setData)
      .catch((e) => setErr(e?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (s3_key) => {
    try { const r = await api.blobDownloadUrl(s3_key); if (r?.url) window.open(r.url, "_blank"); }
    catch (e) { setErr(e?.message || "Could not open file"); }
  };

  if (loading) return <Spinner />;

  const items = data?.items || [];
  const storageDisabled = data && !data.enabled;
  return (
    <div className="kb-fade-up">
      <PageHeader eyebrow="Secure" title="Document vault" />
      <p className="text-sm text-subtle -mt-3 mb-5 max-w-2xl">Encrypted originals of every uploaded EOB and visit recording. Links expire after 5 minutes.</p>

      {err && (
        <Card className="mb-4">
          <p className="text-sm text-ink mb-3" data-testid="vault-error">We couldn&apos;t load your vault right now. {err}</p>
          <button data-testid="vault-retry" onClick={load} className="text-sm font-semibold text-brand-dark underline">Try again</button>
        </Card>
      )}

      {storageDisabled && !err && (
        <Card className="mb-4 bg-mint-tint border-mint-dark"><p className="text-sm text-ink" data-testid="vault-storage-disabled">Encrypted storage isn&apos;t configured in this environment — document originals are available in the production app.</p></Card>
      )}

      {!err && !storageDisabled && (
        items.length === 0 ? (
          <Card><EmptyState testid="vault-empty" icon={Lock} title="No documents yet"
            body="EOBs and visit recordings you upload will be stored here, encrypted at rest." /></Card>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <Card key={it.id}>
                <button data-testid={`vault-open-${it.id}`} onClick={() => open(it.s3_key)} className="w-full flex items-center gap-3 text-left">
                  <div className="w-10 h-10 rounded-lg bg-brand-tint flex items-center justify-center shrink-0">
                    {it.kind === "visit" ? <Mic className="w-5 h-5 text-brand-dark" /> : <FileText className="w-5 h-5 text-brand-dark" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink truncate">{it.title}</div>
                    <div className="text-xs text-subtle">{it.subtitle}{it.subtitle && " · "}{fmtDate(it.created_at)}{prettySize(it.size_bytes) && ` · ${prettySize(it.size_bytes)}`}</div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-brand" />
                </button>
              </Card>
            ))}
          </div>
        )
      )}
    </div>
  );
}
